import React, { useCallback, useState } from "react";
import {
  ExclamationTriangleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import Button, { ButtonColorTypes, ButtonTypes } from "./Button";
import { Modal } from "./Modal";
import { classNames } from "../utils";

export type ConfirmDialogActions = "close" | "continue";

export enum ConfirmationDialogIcon {
  WARN = "WARN",
  DELETE = "DELETE",
}

const iconComponents: Record<ConfirmationDialogIcon, JSX.Element> = {
  [ConfirmationDialogIcon.WARN]: (
    <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center bg-orange-50">
      <ExclamationTriangleIcon className="w-6 h-6 text-orange-500 stroke-[2]" />
    </div>
  ),
  [ConfirmationDialogIcon.DELETE]: (
    <div className="flex-shrink-0 w-10 h-10 rounded-full border-2 border-red-400 flex items-center justify-center bg-transparent">
      <TrashIcon className="w-5 h-5 text-red-500" />
    </div>
  ),
};

type Props = {
  isOpen: boolean;
  title: string;
  text: string;
  actionHandler: (action: ConfirmDialogActions) => void;
  children?: JSX.Element;
  showActionButtons?: boolean;
  hideCloseButton?: boolean;
  cancelButtonText?: string;
  cancelButtonColorType?: ButtonColorTypes;
  confirmButtonText?: string;
  confirmButtonColorType?: ButtonColorTypes;
  confirmDisabled?: boolean;
  isConfirmLoading?: boolean;
  icon?: ConfirmationDialogIcon;
};

export const ConfirmationDialog = ({
  isOpen,
  title,
  text,
  actionHandler,
  children,
  showActionButtons,
  hideCloseButton = false,
  cancelButtonText = "Cancel",
  cancelButtonColorType = ButtonColorTypes.cancel,
  confirmButtonText = "Continue",
  confirmButtonColorType = ButtonColorTypes.primary,
  confirmDisabled = false,
  isConfirmLoading = false,
  icon,
}: Props) => {
  function cancel() {
    actionHandler("close");
  }

  if (!title && !text && !children) return <></>;
  const modalIcon = icon !== undefined ? iconComponents[icon] : null;
  return (
    <Modal
      open={isOpen}
      onClose={cancel}
      title={title}
      width={"w-full max-w-xl"}
      icon={modalIcon}
      hideCloseButton={hideCloseButton}
    >
      <div
        className={classNames(
          "pt-2 pb-6",
          modalIcon ? "pl-[60px] pr-6" : "px-6"
        )}
      >
        {!children && (
          <p className="text-gray-600 text-sm leading-relaxed mb-6">{text}</p>
        )}
        {!text && children}
        {showActionButtons && (
          <div className="flex gap-3 justify-end">
            <Button
              text={cancelButtonText}
              colorType={cancelButtonColorType}
              onClick={cancel}
              className="border-gray-300 rounded-lg"
              disabled={isConfirmLoading}
            />
            <Button
              text={confirmButtonText}
              colorType={confirmButtonColorType}
              onClick={() => actionHandler("continue")}
              className="rounded-lg"
              disabled={confirmDisabled || isConfirmLoading}
              type={isConfirmLoading ? ButtonTypes.saving : ButtonTypes.default}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

type CofirmationDialogState = {
  title: string;
  text: string;
  isOpen: boolean;
  onAction?: (action: ConfirmDialogActions) => void;
  children?: JSX.Element;
  showActionButtons?: boolean;
  hideCloseButton?: boolean;
  cancelButtonText?: string;
  cancelButtonColorType?: ButtonColorTypes;
  confirmButtonText?: string;
  confirmButtonColorType?: ButtonColorTypes;
  icon?: ConfirmationDialogIcon;
};

export const useConfirmationDialog = (hideOnActionClose: boolean = true) => {
  const [confirmationState, setConfirmationState] =
    useState<CofirmationDialogState>({
      title: "",
      text: "",
      isOpen: false,
      showActionButtons: true,
      cancelButtonText: "Cancel",
      cancelButtonColorType: ButtonColorTypes.cancel,
      confirmButtonText: "Continue",
      confirmButtonColorType: ButtonColorTypes.primary,
      icon: undefined,
    });

  const show = useCallback(
    (
      title: string,
      text: string,
      onAction: (action: ConfirmDialogActions) => void,
      children?: JSX.Element,
      showActionButtons: boolean = true,
      cancelButtonText: string = "Cancel",
      cancelButtonColorType: ButtonColorTypes = ButtonColorTypes.cancel,
      confirmButtonText: string = "Continue",
      confirmButtonColorType: ButtonColorTypes = ButtonColorTypes.primary,
      icon?: ConfirmationDialogIcon,
      hideCloseButton: boolean = false
    ) => {
      setConfirmationState({
        title,
        text,
        onAction,
        isOpen: true,
        children,
        showActionButtons,
        hideCloseButton,
        cancelButtonText,
        cancelButtonColorType,
        confirmButtonText,
        confirmButtonColorType,
        icon,
      });
    },
    []
  );

  const hide = useCallback(() => {
    setConfirmationState((prevState) => ({
      ...prevState,
      isOpen: false,
    }));
  }, []);

  function actionHandler(action: ConfirmDialogActions) {
    if (hideOnActionClose && action === "close") {
      hide();
    }
    confirmationState.onAction?.(action);
  }

  const modal = (
    <ConfirmationDialog
      title={confirmationState.title}
      text={confirmationState.text}
      isOpen={confirmationState.isOpen}
      actionHandler={actionHandler}
      showActionButtons={confirmationState.showActionButtons}
      hideCloseButton={confirmationState.hideCloseButton}
      cancelButtonText={confirmationState.cancelButtonText}
      cancelButtonColorType={confirmationState.cancelButtonColorType}
      confirmButtonText={confirmationState.confirmButtonText}
      confirmButtonColorType={confirmationState.confirmButtonColorType}
      icon={confirmationState.icon}
    >
      {confirmationState.children}
    </ConfirmationDialog>
  );
  return { modal, show, hide };
};
